import { createSelector } from '@ngrx/store';
import { OnsiteActivity, OnsiteSchedule } from 'globals/store/onsite.data';
import { Program } from 'globals/store/programs.data';
import { Performer } from 'globals/store/performers.data';
import { ShareOSSInfo } from 'globals/store/share.data';
import { getFullBriefTimeline } from 'globals/share/onsite.reselector.functions';
import { LabelMaps, Ranking } from 'globals/store/events.data';
import { WorkforceGroup, WFWorker } from 'globals/store/workforce.data';
import { Sponsor } from 'globals/store/sponsors.data';
import { Exhibitor, ExhibitorStand } from 'globals/store/exhibitors.data';
import { WorkerWithProfile } from 'globals/store/workforce.data';
import { TagPanelData, getTagPanelData } from 'globals/store/tags.data';
import { eventStateSelector, JoiEvent } from 'src/app/store/events.era';
import { Dictionary } from '@ngrx/entity';
import { JoiResource } from 'globals/store/resources.data';
import { updateProgramsAndVenuesInPlace } from 'globals/helpers/helpers';
import { JoiAccount } from 'globals/store/accounts.data';
import { fullProgramOrderReselector } from './programs.reselector';
import { Attendee } from 'globals/store/attendees.data';
import { getFullSharedFilteredOSSTimelineAndShareableAndPerformers } from 'globals/share/onsite.reselector.functions';
import { getOnsiteSchedules } from 'globals/share/onsite.getoss';
import { onsiteStateSelector } from '../onsite.era';
import { programsStateSelector } from '../programs.era';
import { performersStateSelector } from '../performers.era';
import { shareStateSelector } from '../share.era';
import { workforceStateSelector } from '../workforce.era';
import { accountStateSelector } from '../accounts.era';
import { sponsorsStateSelector } from '../sponsors.era';
import { exhibitorsStateSelector } from '../exhibitors.era';
import { resourcesStateSelector } from '../resources.era';
import { exhibitorStandsStateSelector } from '../exhibitor.stands.era';
import { assetsStateSelector } from '../assets.era';
import { attendeesStateSelector } from '../attendees.era';

const activities = createSelector(onsiteStateSelector, (state) => state.activities)
const schedules = createSelector(onsiteStateSelector, (state) => state.schedules)
const programs = createSelector(programsStateSelector, (state) => Object.values(state.entities))
const performers = createSelector(performersStateSelector, (state) => state.entities)
const onsiteShareables = createSelector(shareStateSelector, (state) => state.oss)
const labelMaps = createSelector(eventStateSelector, (state) => state.labelMaps)
const workforceGroupsById = createSelector(workforceStateSelector, (state) => state.workforceGroupsById)
const workersByEmail = createSelector(workforceStateSelector, (state) => state.workersByEmail)
const event = createSelector(eventStateSelector, (state) => state.event)
const account = createSelector(accountStateSelector, (state) => state.account)
const sponsorTypes = createSelector(sponsorsStateSelector, (state) => state.sponsorTypes)
const sponsors = createSelector(sponsorsStateSelector, (state) => state.entities)
const exhibitors = createSelector(exhibitorsStateSelector, (state) => state.entities)
const resources = createSelector(resourcesStateSelector, (state) => state.entities)
const stands = createSelector(exhibitorStandsStateSelector, (state) => state.entities)
const assets = createSelector(assetsStateSelector, (state) => state.entities)
const activitiesLoaded = createSelector(onsiteStateSelector, (state) => state.activitiesLoaded)
const attendees = createSelector(attendeesStateSelector, (state) => state.entities)

export interface ActivityReselectorData extends TagPanelData { activity: OnsiteActivity }
 
export const activitiesReselector = createSelector(
  activities,
  workersByEmail,
  performers,
  sponsors,
  exhibitors,
  resources,
  attendees,
  (activities, workersByEmail: { [email:string]: WorkerWithProfile }, performers: { [id:string]: Performer }, sponsors: { [id:string]: Sponsor }, exhibitors: { [id:string]: Exhibitor }, resources: Dictionary<JoiResource>, attendees): Dictionary<ActivityReselectorData> => {
    const activityDatas: Dictionary<ActivityReselectorData> = {}
    console.log({ activities, workersByEmail, performers, sponsors, exhibitors, resources, attendees }  )
    if (!activities) return activityDatas
    Object.values(activities).forEach(activity => {
      const { id, tags, performerIds, exhibitorIds, sponsorIds, resourceIds, attendeeIds } = activity
      activityDatas[id] = { activity, ...getTagPanelData(tags, performerIds, sponsorIds, exhibitorIds, workersByEmail, performers, sponsors, exhibitors, resourceIds, resources, attendeeIds, attendees) }
    })
    return activityDatas
  }
)

export const activityReselector = createSelector(
  activitiesReselector,
  (activitiesData: Dictionary<ActivityReselectorData>, activityId: string): ActivityReselectorData => {
    return activitiesData[activityId] || null
  }
)

interface BaseOnsiteData { activities: Dictionary<OnsiteActivity>, schedules: Dictionary<OnsiteSchedule>, programs: Program[], stands: Dictionary<ExhibitorStand>, account: Partial<JoiAccount>, fullProgramOrder: string[] }
const baseOnsiteReselector = createSelector(
  activities,
	schedules,
  programs,
  stands,
  account,
  fullProgramOrderReselector,
  (activities, schedules, programs: Program[], stands, account, fullProgramOrder): BaseOnsiteData => ({ activities, schedules, programs, stands, account, fullProgramOrder })
)

export const onsiteReselector = createSelector(
  baseOnsiteReselector,
  activitiesLoaded,
  ({ activities, schedules, programs }: BaseOnsiteData, activitiesLoaded) => getOnsiteSchedules(Object.values(activities), Object.values(schedules), programs, activitiesLoaded)
	
)

export interface briefTimelineReselectorExtraInputs { 
  performerId?: string
  exhibitorId?: string
  resourceId?: string
  attendeeId?: string
}

export const briefsTimelineReselector = createSelector(
  baseOnsiteReselector,
  exhibitors,
  performers,
  resources,
  assets,
  event,
  fullProgramOrderReselector,
  ({ activities, schedules, programs, stands }: BaseOnsiteData, exhibitors: { [eId:string]: Exhibitor }, performers: { [pId:string]: Performer}, resources, assets, event: JoiEvent, eventProgramOrder: string[], { performerId, exhibitorId, resourceId, attendeeId }: briefTimelineReselectorExtraInputs) => {
    updateProgramsAndVenuesInPlace(event.venues, programs)
    return getFullBriefTimeline({ activities: Object.values(activities), schedules: Object.values(schedules), programs, performerId, exhibitorId, exhibitors: exhibitorId ? exhibitors : null, performers, resources, resourceId, stands, assets, eventProgramOrder, attendeeId })
  }
)

interface BaseOnsiteEntitiesData { performers: { [pId: string]: Performer }, sponsors: { [sId: string]: Sponsor }, sponsorTypes: { [tId:string]: Ranking }, exhibitors: { [eId: string]: Exhibitor}, resources: { [rId: string]: JoiResource }, attendees: { [aId: string]: Attendee } }

const baseEntitiesReselector = createSelector(
  performers,
  sponsors,
  sponsorTypes,
  exhibitors,
  resources,
  attendees,
  (performers: { [pId: string]: Performer }, sponsors: { [sId: string]: Sponsor }, sponsorTypes: { [tId:string]: Ranking }, exhibitors: { [eId: string]: Exhibitor}, resources: { [rId: string]: JoiResource }, attendees): BaseOnsiteEntitiesData => ({ performers, sponsors, sponsorTypes, exhibitors, resources, attendees })
)

export const sharedOnsiteTimelineReselector = createSelector(
  baseOnsiteReselector,
  baseEntitiesReselector, // have to use these "base" reselectors because a reselector is limited to 9 inputs, this is the workaround
  onsiteShareables,
  labelMaps,
  workforceGroupsById,
  workersByEmail,
  event,
  assets,
  ({ activities, schedules, programs, stands, account, fullProgramOrder }: BaseOnsiteData, { performers, sponsors, sponsorTypes, exhibitors, resources, attendees }: BaseOnsiteEntitiesData, onsiteShareables: {[id:string]:ShareOSSInfo}, labelMaps: LabelMaps, workforceGroupsById: { [id:string]: WorkforceGroup }, workersByEmail: { [key:string]: WFWorker }, event: JoiEvent, assets, shareableId: string) => {
    return getFullSharedFilteredOSSTimelineAndShareableAndPerformers(Object.values(activities), Object.values(schedules), programs, onsiteShareables[shareableId], event, performers, labelMaps, workforceGroupsById, workersByEmail, sponsors, sponsorTypes, exhibitors, stands, assets, resources, account, fullProgramOrder, attendees) 
  }
)


